import ELK from 'elkjs/lib/elk.bundled.js'
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import {
    ReactFlow,
    addEdge,
    Panel,
    useNodesState,
    useEdgesState,
    useReactFlow,
    Controls,
    MiniMap,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import '../../App.css'
import DepartmentNode from './departmentNode'
import PositionNode from './positionNode'
import CustomEdge from './CustomEdge'
import RequestQueue from '../../api/requestQueue'
import { toast } from 'react-toastify'
import Loading from '../../component/loading'
import ModalAddPosition from '../../component/modals/modalAddPosition'
import ModalEditPosition from '../../component/modals/modalEditPosition'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate, faFloppyDisk } from '@fortawesome/free-solid-svg-icons'

const findNonTargetedNodes = (nodes, edges) => {
    const nodeIds = nodes.map((node) => node.id)
    const targetIds = edges.map((edge) => edge.target)

    const nonTargetedNodeIds = nodeIds.filter((id) => !targetIds.includes(id))

    return nonTargetedNodeIds
}

const nodeTypes = {
    DepartmentNode,
    PositionNode,
}

const edgeTypes = {
    'custom-edge': CustomEdge,
}

const elk = new ELK()
const elkOptions = {
    'elk.algorithm': 'layered',
    'elk.layered.spacing.nodeNodeBetweenLayers': '100',
    'elk.spacing.nodeNode': '80',
}

const convertDataToNodesAndEdges = (
    departments,
    positions,
    handlerAddPosition,
    handleIcon2Click,
    handlerEditPosition,
    handlerDeletePosition
) => {
    const nodes = []
    const edges = []

    // ایجاد نودها برای بخش کاری‌ها
    departments.forEach((department) => {
        nodes.push({
            id: department.id,
            data: {
                label: department.name,
                handlerAddPosition: () =>
                    handlerAddPosition(null, department.id),
                onClickIcon2: () =>
                    handleIcon2Click(department.id, department.parent_id),
            },
            type: 'DepartmentNode', // تغییر نوع به 'department'
            position: { x: 0, y: 0 },
        })
    })

    // ایجاد نودها برای موقعیت‌ها
    positions.forEach((position) => {
        nodes.push({
            id: position.id,
            data: {
                label: position.name,
                onClickIconPosition: () =>
                    handlerAddPosition(position.id, position.department_id),

                onClickEditPosition: () => handlerEditPosition(position),

                onClickDeletePosition: () => handlerDeletePosition(position.id),
            },
            type: 'PositionNode',
            position: { x: 0, y: 0 },
        })
    })

    // ایجاد لبه‌ها برای ارتباطات بین بخش کاری‌ها و موقعیت‌ها
    positions.forEach((position) => {
        if (position.parent_id) {
            edges.push({
                id: `e${position.id}`,
                source: position.parent_id,
                target: position.id,
                type: 'custom-edge',
                animated: true,
                data: position,
            })
        } else {
            edges.push({
                id: `e${position.department_id}-${position.id}`,
                source: position.department_id,
                target: position.id,
                type: 'custom-edge',
                animated: true,
                data: position,
            })
        }
    })

    return { nodes, edges }
}

const getLayoutedElements = (nodes, edges, options = {}) => {
    const isHorizontal = options?.['elk.direction'] === 'RIGHT'

    const graph = {
        id: 'root',
        layoutOptions: options,
        children: nodes.map((node) => ({
            ...node,
            targetPosition: isHorizontal ? 'left' : 'top',
            sourcePosition: isHorizontal ? 'right' : 'bottom',
            // width: node.data.label.length * 15, // تنظیم عرض بر اساس طول برچسب
            width: node.data.label.length * 15, // تنظیم عرض بر اساس طول برچسب
            height: 60, // تنظیم ارتفاع به صورت ثابت
        })),
        edges: edges,
    }

    return elk
        .layout(graph)
        .then((layoutedGraph) => ({
            nodes: layoutedGraph.children.map((node) => ({
                ...node,
                position: { x: node.x, y: node.y },
            })),
            edges: layoutedGraph.edges,
        }))
        .catch(console.error)
}

function Position2Layout({ selectedDepartment }) {
    const [nodes, setNodes, onNodesChange] = useNodesState([])
    const [edges, setEdges, onEdgesChange] = useEdgesState([])
    const { fitView } = useReactFlow()
    const [loading, setLoading] = useState(false)

    const [departmentData, setDepartmentData] = useState([selectedDepartment])

    const [positionData, setPositionData] = useState([])

    const [updatedPositionData, setUpdatedPositionData] = useState([])

    useEffect(() => {
        if (selectedDepartment) {
            getPosition()
        }
    }, [selectedDepartment])

    const getPosition = async () => {
        setLoading(true)

        await RequestQueue.sendRequest({
            config: {
                method: 'get',
                url: `/position`,
            },
            resolve: (res) => {
                const dataP = res.data.filter(
                    (pos) => pos.department_id === selectedDepartment.id
                )
                setPositionData(dataP)
                setUpdatedPositionData(dataP)
                setDepartmentData([selectedDepartment])
                setLoading(false)
            },
            reject: (error) => {
                setLoading(false)
                toast.dismiss()
                toast.error('خطا در ارتباط با سرور', {
                    position: 'bottom-center',
                })
            },
        })
    }

    const handlerAddPosition = (positionId, departmentId) => {
        setIdAddDepartmentId(departmentId)
        setIdAddPositionId(positionId)
        setShowModalAddPosition((prev) => !prev)
        // console.log(`Icon 1 clicked for department ${id} with parent ${parentId}`);
        // Add your custom logic here
    }

    const handleIcon2Click = (id, parentId) => {
        console.log(
            `Icon 2 clicked for department ${id} with parent ${parentId}`
        )
        // Add your custom logic here
    }

    const onConnect = useCallback(
        (connection) => {
            let edge = { ...connection, type: 'custom-edge' }
            const idConnection = edge.target
            const parentConnection = edge.source

            const searchDepartment = departmentData.find(
                (dep) => dep.id === idConnection
            )
            const searchPosition = positionData.find(
                (pos) => pos.id === idConnection
            )

            let nameConnection = ''
            if (searchDepartment) {
                nameConnection = searchDepartment.name
            } else if (searchPosition) {
                nameConnection = searchPosition.name
            }

            edge.data = {
                id: idConnection,
                name: nameConnection,
                parent_id: parentConnection,
            }

            setEdges((eds) => addEdge(edge, eds))

            // Update department data
            const updatedData = updatedPositionData.map((dep) => {
                if (
                    dep.id === idConnection &&
                    selectedDepartment.id !== parentConnection
                ) {
                    return { ...dep, parent_id: parentConnection }
                }
                return dep
            })
            setUpdatedPositionData(updatedData)
        },
        [setEdges, departmentData, positionData, updatedPositionData]
    )

    const onLayout = useCallback(
        ({ direction }) => {
            if (selectedDepartment) {
                const opts = { 'elk.direction': direction, ...elkOptions }
                const { nodes: initialNodes, edges: initialEdges } =
                    convertDataToNodesAndEdges(
                        departmentData,
                        positionData,
                        handlerAddPosition,
                        handleIcon2Click,
                        handlerEditPosition,
                        handlerDeletePosition
                    )
                console.log(departmentData, positionData, 'nodes,edges')
                getLayoutedElements(initialNodes, initialEdges, opts).then(
                    ({ nodes: layoutedNodes, edges: layoutedEdges }) => {
                        setNodes(layoutedNodes)
                        setEdges(layoutedEdges)
                        window.requestAnimationFrame(() => fitView())
                    }
                )
            }
        },
        [
            setNodes,
            setEdges,
            fitView,
            departmentData,
            positionData,
            selectedDepartment,
        ]
    )

    useLayoutEffect(() => {
        onLayout({ direction: 'DOWN' })
    }, [onLayout])

    const [idAddPositionId, setIdAddPositionId] = useState('')
    const [idAddDepartmentId, setIdAddDepartmentId] = useState('')
    const [showModalAddPosition, setShowModalAddPosition] = useState(false)
    const [showModalEditPosition, setShowModalEditPosition] = useState(false)
    const [idPositionSelected, setIdPositionSelected] = useState()

    const updateConnectionPosition = async () => {
        const changes = updatedPositionData.filter((updatedDep) => {
            const originalDep = positionData.find(
                (dep) => dep.id === updatedDep.id
            )
            return originalDep && originalDep.parent_id !== updatedDep.parent_id
        })
        for (const change of changes) {
            try {
                setLoading(true)
                await RequestQueue.sendRequest({
                    config: {
                        method: 'patch',
                        url: `/position/${change.id}`,
                        data: {
                            parent_id: change.parent_id,
                            name: change.name,
                            department_id: change.department_id,
                        },
                    },
                    resolve: (res) => {
                        setLoading(false)
                    },
                    reject: (error) => {
                        setLoading(false)
                        toast.dismiss()
                        toast.error('خطا در ارتباط با سرور', {
                            position: 'bottom-center',
                        })
                    },
                })
            } catch (error) {
                setLoading(false)
                toast.dismiss()
                toast.error('خطا در ارتباط با سرور', {
                    position: 'bottom-center',
                })
            }
        }

        // Refresh department data after updates
        getPosition()
    }

    useEffect(() => {
        const nonTargetedNodeIds = findNonTargetedNodes(nodes, edges)
        console.log('Non-targeted Node IDs:', nonTargetedNodeIds)

        const updatedData = updatedPositionData.map((dep) => {
            if (nonTargetedNodeIds.includes(dep.id)) {
                return { ...dep, parent_id: null }
            }
            return dep
        })

        setUpdatedPositionData(updatedData)
    }, [edges])

    const handlerEditPosition = (p) => {
        setShowModalEditPosition(true)
        setIdPositionSelected(p.id)
    }

    const handlerDeletePosition = async (id) => {
        setLoading(true)
        await RequestQueue.sendRequest({
            config: {
                method: 'delete',
                url: `/position/${id}`,
            },
            resolve: (res) => {
                setLoading(false)
                getPosition()
            },
            reject: (error) => {
                setLoading(false)
                toast.dismiss()
                toast.error('خطا در ارتباط با سرور')
            },
        })
    }

    return (
        <>
            {loading ? <Loading /> : null}
            {showModalAddPosition ? (
                <ModalAddPosition
                    showModal={showModalAddPosition}
                    setShowModal={setShowModalAddPosition}
                    handlerSubmit={() => {}}
                    loadData={() => {
                        getPosition()
                    }}
                    idAddDepartmentId={idAddDepartmentId}
                    idAddPositionId={idAddPositionId}
                />
            ) : null}
            {showModalEditPosition ? (
                <ModalEditPosition
                    showModal={showModalEditPosition}
                    setShowModal={setShowModalEditPosition}
                    handlerSubmit={() => {}}
                    loadData={() => {
                        getPosition()
                    }}
                    idAddDepartmentId={idAddDepartmentId}
                    idAddPositionId={idAddPositionId}
                    id={idPositionSelected}
                />
            ) : null}
            <div className="flex w-100 justify-end">
                <button
                    className="p-4 rounded-md m-1 green-clr-bg white-clr flex items-center"
                    onClick={updateConnectionPosition}
                >
                    <FontAwesomeIcon icon={faFloppyDisk} className="ml-2" />
                    ذخیره
                    <div className="w-2 h-2 mr-2 rounded-full bg-white animate-ping"></div>
                </button>
            </div>

            <ReactFlow
                nodes={nodes}
                edges={edges}
                onConnect={onConnect}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                fitView
                id="flowTree"
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                style={{ width: '100%', height: '100vh' }}
            >
                <Panel position="top-right">
                    <button
                        className="axis-flow-btn"
                        onClick={() => onLayout({ direction: 'DOWN' })}
                    >
                        نمایش عمودی
                    </button>
                    <button
                        className="axis-flow-btn"
                        onClick={() => onLayout({ direction: 'RIGHT' })}
                    >
                        نمایش افقی
                    </button>
                </Panel>
                <MiniMap />
                <Controls />
            </ReactFlow>
        </>
    )
}

export default Position2Layout
